import { FilePurpose } from '@/api/enums';
import { isKeycloakEnabled } from '@/auth';
import { client } from '@/client';
import { ResourceType } from '@/client/courses';
import { File, FileExtensions } from '@/client/files';
import { COURSE_FILE_MAX_SIZE } from '@/common/constants/files';
import { DateFormats } from '@/system-settings/enums';
import moment from 'moment';
import {
  FileUpload,
  FileUploadHeaderTemplateOptions,
  FileUploadUploadEvent,
} from 'primereact/fileupload';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import styled from 'styled-components';
import { FlexContainer } from '../styled-ui';

const StyledUploadWrapper = styled(FlexContainer)`
  background: var(--beige-main);
  border-radius: 4px;
  min-width: 310px;
  min-height: 90px;
  padding: 0;
  word-break: break-word;

  .icon {
    color: var(--gray-darker);
    font-size: var(--body-font-size);
  }

  p {
    margin: 0;
  }

  p,
  .p-button-label {
    color: var(--gray-darker);
    font-size: var(--xsmall-font-size);
    line-height: var(--xsmall-line-height);
  }

  .p-button-label {
    color: var(--black-main);
  }

  .p-fileupload {
    width: 100%;
    border: none;
    position: relative;
    background-color: transparent;

    &:has(.p-fileupload-highlight) {
      background-color: var(--beige-main);
      i {
        color: var(--green-dark);
      }
    }

    &:has(.file-uploaded) {
      border-color: var(--green-dark);
      background-color: var(--green-pale);
      i {
        color: var(--green-dark);
      }
    }
  }
  .p-fileupload-buttonbar {
    border: none;
    font-size: var(--small-font-size);
    font-weight: 500;
    color: var(--dark-gray);
    background: transparent;
  }

  .p-fileupload-choose {
    background-color: transparent;
    color: var(--black-main);
    border: none;
    font-weight: 600;
    font-size: var(--small-font-size);
    padding: 0px;
    z-index: 100;

    .p-button-icon {
      margin: 0;
    }
  }

  .p-fileupload-content {
    position: absolute;
    top: 0;
    width: 100%;
    border: none;
    background: transparent;
  }

  .p-progressbar {
    padding-inline: var(--default-padding);
    background-color: transparent;
    .p-progressbar-value {
      background-color: var(--gray-darker);
    }
  }

  .p-fileupload-row {
    display: none;
  }
`;

const StyledError = styled.p`
  color: var(--red-main);
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
  margin: 0;
  width: 100%;
`;

const StyledFileBox = styled(FlexContainer)`
  display: flex;
  justify-content: center;
  flex-direction: column;
  border-radius: var(--xxxsmall-border-radius);
  border: 1px solid var(--gray-dark);
  padding: var(--xsmall-padding) var(--small-padding);
  color: var(--black-main);
  min-height: 80px;
  font-weight: 500;

  p {
    max-width: 150px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
`;

const StyledTextSpan = styled.span`
  font-size: var(--xxsmall-font-size);
  line-height: var(--small-line-height);
  color: var(--black-main);
  bottom: 3px;
  right: 48px;
`;

type CourseFileUploadProps = {
  fileType?: ResourceType;
  preselectedFile?: File | null;
  purpose?: FilePurpose;
  selectedLanguage?: string;
  isDisabled?: boolean;
  onFileUpload: (file: File | null) => void;
  setIsTabsDisabled: (isUploading: boolean) => void;
};

export const ContentFileUpload: React.FC<CourseFileUploadProps> = ({
  fileType,
  preselectedFile,
  purpose,
  selectedLanguage,
  isDisabled = false,
  onFileUpload,
  setIsTabsDisabled,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isKeycloakAuth = isKeycloakEnabled();
  const [error, setError] = useState<string[]>([]);

  const [isDragEnter, setIsDragEnter] = useState(false);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const fileUploadRef = useRef<FileUpload | null>(null);

  useEffect(() => {
    setSelectedFile(preselectedFile || null);
  }, [preselectedFile]);

  const onUpload = async (e: FileUploadUploadEvent) => {
    const response = JSON.parse(e?.xhr?.response);

    setSelectedFile(response);

    setError([]);

    onFileUpload(response);
    setIsUploading(false);
    setIsTabsDisabled(false);
  };

  const clearUpload = () => {
    setSelectedFile(null);
    onFileUpload(null);
    setError([]);
  };

  const getAllowedFileTypes = () => {
    switch (fileType) {
      case ResourceType.PDF:
        return FileExtensions.PDF.join(', ');
      case ResourceType.VIDEO:
        return FileExtensions.VIDEO.join(', ');
      case ResourceType.SCORM:
        return FileExtensions.SCORM.join(', ');
      default:
        return '';
    }
  };

  // Using created date to show last updated date, because on upload / reupload new file object is created.
  const FileLastUpdated = () => {
    if (
      preselectedFile &&
      preselectedFile?.signedUrl &&
      preselectedFile?.created &&
      !isDisabled
    )
      return (
        <StyledTextSpan className="mt-1">
          {t('course.file.lastUpdated', {
            date: moment(preselectedFile?.created).format(DateFormats.DATEONLY),
          })}
        </StyledTextSpan>
      );
  };

  const headerTemplateEnter = (options: FileUploadHeaderTemplateOptions) => {
    const { className } = options;

    return (
      <FlexContainer className={className} direction="column" height="100%">
        <i className="pi pi-upload icon"></i>
        <p style={{ textAlign: 'center' }}>{t('content.file.upload.drop')}</p>
      </FlexContainer>
    );
  };

  const headerTemplateLeave = (options: FileUploadHeaderTemplateOptions) => {
    const { className, chooseButton } = options;

    return (
      <FlexContainer
        className={className}
        direction="column"
        height="100%"
        gap={12}
      >
        <i className="pi pi-upload icon"></i>
        <p style={{ textAlign: 'center' }}>
          {t('content.file.upload', {
            allowedFileTypes: getAllowedFileTypes(),
          })}{' '}
          {chooseButton}
        </p>
      </FlexContainer>
    );
  };

  return (
    <FlexContainer gap={24} justify="space-between" direction="column">
      <FlexContainer
        gap={20}
        align="initial"
        onDragOverCapture={() => {
          setIsDragEnter(true);
        }}
        onDragLeaveCapture={() => setIsDragEnter(false)}
      >
        <StyledUploadWrapper flex="1">
          {!isDisabled && (
            <FileUpload
              ref={fileUploadRef}
              mode="advanced"
              headerTemplate={
                isDragEnter ? headerTemplateEnter : headerTemplateLeave
              }
              name="file"
              url={client.files.getFileUploadUrl()}
              accept={getAllowedFileTypes()}
              maxFileSize={COURSE_FILE_MAX_SIZE}
              onUpload={onUpload}
              onValidationFail={(file) => {
                if (file.size >= COURSE_FILE_MAX_SIZE) {
                  setError((state) => [
                    t('error.fileSize', {
                      mb: COURSE_FILE_MAX_SIZE / (1000 * 1000),
                    }),
                    ...state,
                  ]);
                }
                setIsDragEnter(false);
              }}
              onError={(event) => {
                const response = JSON.parse(event?.xhr?.response);

                if (response?.message) {
                  setError((state) => ['Incorrect file type', ...state]);
                }
                setIsUploading(false);
                setIsTabsDisabled(false);
                setIsDragEnter(false);
              }}
              auto
              chooseLabel={t('content.file.upload.browse')}
              chooseOptions={{ icon: 'none' }}
              withCredentials
              onBeforeSend={(event) => {
                if (user && isKeycloakAuth) {
                  event.xhr.setRequestHeader(
                    'Authorization',
                    `Bearer ${user?.access_token}`,
                  );
                }
              }}
              onBeforeUpload={(event) => {
                clearUpload();
                setIsUploading(true);
                setIsTabsDisabled(true);

                event.formData.append('purpose', purpose || FilePurpose.COURSE);
              }}
              disabled={isUploading}
            />
          )}
        </StyledUploadWrapper>

        <FlexContainer flex="1" direction="column" gap={17}>
          <strong className="black">
            {t('content.selected.language', { language: selectedLanguage })}
          </strong>

          {selectedFile?.id ? (
            <StyledFileBox>
              {t('content.current.file.uploaded')} <br />
              <p className="text-center m-0">{selectedFile?.fileName}</p>
              {FileLastUpdated()}
            </StyledFileBox>
          ) : (
            <StyledFileBox>
              <p className="text-center">{t('content.no.file.uploaded')}</p>
            </StyledFileBox>
          )}
        </FlexContainer>
      </FlexContainer>

      {!!error?.length && (
        <StyledError>
          {error.map((error, index) => (
            <span key={index}>
              {error}.<br />
            </span>
          ))}
        </StyledError>
      )}
    </FlexContainer>
  );
};
